import { getNumber, formatCPF } from '@utils/Format'
import { BROKERS } from '@modules/ws/constants/Brokers'
import { TRACKING_STATUS } from '@modules/ws/constants/TrackingProcess'

/**
 * Cobrança de taxas de administração (spec B5) — fluxo estruturalmente
 * diferente dos outros 5 processos de Backoffice (onboarding, migração,
 * custódia, profissional): não tem `etapas` fixas, é um lote de upload com
 * status POR CLIENTE. Este arquivo é a fonte única de verdade do shape do
 * template CSV, do enum de status por cliente e da derivação do status do
 * lote — reusado por `apiWsBackofficeFeeBilling.js`, `mocks/mockFeeBilling.js`
 * e os componentes de `components/backoffice/fee-billing-*`.
 *
 * @typedef {Object} FeeBillingCliente
 * @property {number|string} id
 * @property {string} cpf - o template não traz nome do cliente (spec B5:
 *   "CPF, conta, instituição, valor da taxa, competência") — CPF é o
 *   identificador exibido na tabela de status e nas pendências agregadas
 * @property {string} conta
 * @property {string} instituicao - ver BROKERS/MAP_BROKERS de ws/constants/Brokers
 * @property {number} valor
 * @property {string} competencia - formato MM/AAAA
 * @property {string} status - um dos valores de FEE_BILLING_CLIENT_STATUS
 * @property {string|null} motivo - texto legível do resultado (preenchido
 *   quando `status` não é `cobrado`)
 *
 * @typedef {Object} FeeBillingLote
 * @property {number|string} id
 * @property {string} instituicao - instituição única do lote, ou
 *   `INSTITUICAO_MULTIPLA_LABEL` quando os clientes têm instituições distintas
 * @property {string} competencia - competência única do lote, ou
 *   `COMPETENCIA_MULTIPLA_LABEL` quando os clientes têm competências distintas
 * @property {string} nome_arquivo
 * @property {string} data_upload - YYYY-MM-DD
 * @property {number} total_clientes
 * @property {string} status - um dos valores de TRACKING_STATUS
 * @property {number} progresso - 0-100
 * @property {FeeBillingCliente[]} clientes
 */

// Colunas do template CSV client-side (spec B5) — mesma ordem usada para
// gerar o template (`downloadCsv`) e para mapear as colunas ao parsear o
// arquivo enviado de volta (`fee-billing-upload.vue`). Fonte única: mudar
// aqui muda os dois lados ao mesmo tempo.
export const FEE_BILLING_TEMPLATE_HEADERS = [
  { text: 'CPF', value: 'cpf' },
  { text: 'Conta', value: 'conta' },
  { text: 'Instituição', value: 'instituicao' },
  { text: 'Valor da taxa', value: 'valor' },
  { text: 'Competência', value: 'competencia' },
]

export const FEE_BILLING_CLIENT_STATUS = {
  PENDENTE: 'pendente',
  COBRADO: 'cobrado',
  SEM_SALDO: 'sem_saldo',
  SEM_TERMO: 'sem_termo',
  SALDO_INSUFICIENTE: 'saldo_insuficiente',
  ERRO: 'erro',
}

// Estados que representam uma pendência a resolver (tudo que não é sucesso
// nem "ainda processando") — usados para agregar `pending-issues-list.vue`
// na view e para derivar o status do lote.
export const FEE_BILLING_FAILURE_STATUSES = [
  FEE_BILLING_CLIENT_STATUS.SEM_SALDO,
  FEE_BILLING_CLIENT_STATUS.SEM_TERMO,
  FEE_BILLING_CLIENT_STATUS.SALDO_INSUFICIENTE,
  FEE_BILLING_CLIENT_STATUS.ERRO,
]

export const FEE_BILLING_CLIENT_STATUS_LABEL = {
  [FEE_BILLING_CLIENT_STATUS.PENDENTE]: 'Aguardando processamento',
  [FEE_BILLING_CLIENT_STATUS.COBRADO]: 'Cobrado',
  [FEE_BILLING_CLIENT_STATUS.SEM_SALDO]: 'Sem saldo',
  [FEE_BILLING_CLIENT_STATUS.SEM_TERMO]: 'Não assinou o termo',
  [FEE_BILLING_CLIENT_STATUS.SALDO_INSUFICIENTE]: 'Ficaria negativo',
  [FEE_BILLING_CLIENT_STATUS.ERRO]: 'Erro ao processar',
}

// Motivo legível e mais completo que o label curto acima — exibido no
// tooltip do badge e na coluna "Motivo" da tabela de status por cliente.
export const FEE_BILLING_CLIENT_STATUS_MOTIVO = {
  [FEE_BILLING_CLIENT_STATUS.PENDENTE]: 'Ainda aguardando o processamento do lote.',
  [FEE_BILLING_CLIENT_STATUS.COBRADO]: 'Cobrança realizada com sucesso.',
  [FEE_BILLING_CLIENT_STATUS.SEM_SALDO]: 'Conta sem saldo suficiente para a cobrança.',
  [FEE_BILLING_CLIENT_STATUS.SEM_TERMO]: 'Cliente não assinou o termo de cobrança.',
  [FEE_BILLING_CLIENT_STATUS.SALDO_INSUFICIENTE]:
    'A cobrança deixaria o cliente com saldo negativo.',
  [FEE_BILLING_CLIENT_STATUS.ERRO]: 'Erro ao processar a cobrança — tente novamente.',
}

// Verde = sucesso, amarelo = pendência recuperável (o cliente resolve),
// vermelho = erro de processamento. "pendente" (ainda processando) fica
// neutro — não é nem sucesso nem falha ainda.
export const FEE_BILLING_CLIENT_STATUS_COLOR = {
  [FEE_BILLING_CLIENT_STATUS.PENDENTE]: '#6A7086',
  [FEE_BILLING_CLIENT_STATUS.COBRADO]: '#56AF79',
  [FEE_BILLING_CLIENT_STATUS.SEM_SALDO]: '#E0AF00',
  [FEE_BILLING_CLIENT_STATUS.SEM_TERMO]: '#E0AF00',
  [FEE_BILLING_CLIENT_STATUS.SALDO_INSUFICIENTE]: '#E0AF00',
  [FEE_BILLING_CLIENT_STATUS.ERRO]: '#EB5757',
}

export const FEE_BILLING_CLIENT_STATUS_ICON = {
  [FEE_BILLING_CLIENT_STATUS.PENDENTE]: 'clock',
  [FEE_BILLING_CLIENT_STATUS.COBRADO]: 'check-circle',
  [FEE_BILLING_CLIENT_STATUS.SEM_SALDO]: 'warning-circle',
  [FEE_BILLING_CLIENT_STATUS.SEM_TERMO]: 'warning-circle',
  [FEE_BILLING_CLIENT_STATUS.SALDO_INSUFICIENTE]: 'warning-circle',
  [FEE_BILLING_CLIENT_STATUS.ERRO]: 'close-circle',
}

const FALLBACK_CLIENT_STATUS_COLOR = '#8E8E8E'
const FALLBACK_CLIENT_STATUS_ICON = 'info-circle'

/**
 * Resolve label/motivo/cor/ícone de um `status` de cliente da cobrança, com
 * fallback neutro — mesma forma de `getTrackingStatusInfo`/
 * `getConciliacaoStatusInfo` já usados no módulo.
 * @param {String} status Uma das chaves de FEE_BILLING_CLIENT_STATUS.
 * @returns {{label: String, motivo: String, color: String, icon: String}}
 */
export function getFeeBillingClientStatusInfo(status) {
  const key = status || FEE_BILLING_CLIENT_STATUS.PENDENTE
  return {
    label: FEE_BILLING_CLIENT_STATUS_LABEL[key] || key,
    motivo: FEE_BILLING_CLIENT_STATUS_MOTIVO[key] || '',
    color: FEE_BILLING_CLIENT_STATUS_COLOR[key] || FALLBACK_CLIENT_STATUS_COLOR,
    icon: FEE_BILLING_CLIENT_STATUS_ICON[key] || FALLBACK_CLIENT_STATUS_ICON,
  }
}

export const FEE_BILLING_COMPETENCIA_REGEX = /^(0[1-9]|1[0-2])\/\d{4}$/
const CPF_DIGITS_LENGTH = 11

/**
 * Valida uma linha já parseada do CSV de cobrança (spec B5: "preview com
 * linhas válidas/inválidas + motivo ANTES de confirmar"). Pura e
 * unit-testável isoladamente — reusada por `fee-billing-upload.vue` (preview
 * client-side) e `apiWsBackofficeFeeBilling.importar` (revalidação no
 * "backend" mock, nunca confia só no que a UI already filtrou).
 * @param {Object} row `{cpf, conta, instituicao, valor, competencia}`
 * @returns {{valida: Boolean, motivo: String|null}}
 */
function hasValidCpf(row) {
  return String(row.cpf || '').replaceAll(/\D/g, '').length === CPF_DIGITS_LENGTH
}

function hasValidConta(row) {
  return Boolean(row.conta && String(row.conta).trim())
}

function hasValidInstituicao(row) {
  const instituicao = String(row.instituicao || '').trim()
  return Boolean(instituicao) && BROKERS.includes(instituicao)
}

function hasValidValor(row) {
  const valorNumerico = getNumber(row.valor)
  return Boolean(valorNumerico) && valorNumerico > 0
}

function hasValidCompetencia(row) {
  return FEE_BILLING_COMPETENCIA_REGEX.test(String(row.competencia || '').trim())
}

// Cada regra roda na ordem — a primeira que falhar decide o `motivo`
// devolvido. Lista em vez de if/return em sequência: mantém a função
// principal dentro do limite de complexidade do repo e facilita adicionar
// uma nova regra sem tocar no fluxo de controle.
const ROW_VALIDATIONS = [
  { check: hasValidCpf, motivo: 'CPF inválido ou ausente.' },
  { check: hasValidConta, motivo: 'Conta não informada.' },
  { check: hasValidInstituicao, motivo: 'Instituição não reconhecida.' },
  { check: hasValidValor, motivo: 'Valor da taxa inválido.' },
  { check: hasValidCompetencia, motivo: 'Competência deve estar no formato MM/AAAA.' },
]

export function validateFeeBillingRow(row) {
  if (!row || typeof row !== 'object') {
    return { valida: false, motivo: 'Linha inválida.' }
  }

  const regraQuebrada = ROW_VALIDATIONS.find((regra) => !regra.check(row))
  if (regraQuebrada) {
    return { valida: false, motivo: regraQuebrada.motivo }
  }

  return { valida: true, motivo: null }
}

/**
 * Formata um CPF para exibição (`123.456.789-00`) quando tem 11 dígitos;
 * senão devolve o valor cru (nunca esconde o dado, mesmo mal formatado) ou
 * '—' quando ausente. Usado tanto pela tabela de status por cliente quanto
 * pela agregação de pendências da view (spec B5: CPF é o identificador do
 * cliente — o template não traz nome).
 * @param {String} cpf
 * @returns {String}
 */
export function formatFeeBillingCpf(cpf) {
  if (!cpf) return '—'
  const digits = String(cpf).replaceAll(/\D/g, '')
  return digits.length === CPF_DIGITS_LENGTH ? formatCPF(digits) : String(cpf)
}

const INSTITUICAO_MULTIPLA_LABEL = 'Múltiplas instituições'
const COMPETENCIA_MULTIPLA_LABEL = 'Múltiplas competências'

/**
 * Resolve o valor "de resumo" de um campo por lote a partir dos clientes: se
 * todos compartilham o mesmo valor, usa ele; senão, usa o rótulo de
 * "múltiplos". O template permite instituição/competência por LINHA (não por
 * lote) — um único upload pode misturar instituições/competências.
 * @param {FeeBillingCliente[]} clientes
 * @param {String} campo `'instituicao'` ou `'competencia'`
 * @param {String} multiplaLabel
 * @returns {String}
 */
export function resolveLoteSummaryField(clientes, campo, multiplaLabel) {
  const valores = Array.from(
    new Set((clientes || []).map((cliente) => cliente && cliente[campo]).filter(Boolean))
  )
  if (valores.length === 0) return '—'
  if (valores.length === 1) return valores[0]
  return multiplaLabel
}

export function resolveLoteInstituicao(clientes) {
  return resolveLoteSummaryField(clientes, 'instituicao', INSTITUICAO_MULTIPLA_LABEL)
}

export function resolveLoteCompetencia(clientes) {
  return resolveLoteSummaryField(clientes, 'competencia', COMPETENCIA_MULTIPLA_LABEL)
}

/**
 * Deriva o `status` do lote a partir do status atual de cada cliente:
 * qualquer cliente ainda com falha (`FEE_BILLING_FAILURE_STATUSES`) marca o
 * lote como "com pendência"; enquanto algum cliente ainda está `pendente`
 * (processando), o lote fica "em andamento"; só fica "concluído" quando
 * todos os clientes foram cobrados com sucesso.
 * @param {FeeBillingCliente[]} clientes
 * @returns {String} Uma das chaves de TRACKING_STATUS.
 */
export function deriveFeeBillingLoteStatus(clientes) {
  const lista = clientes || []
  const temFalha = lista.some(
    (cliente) => cliente && FEE_BILLING_FAILURE_STATUSES.includes(cliente.status)
  )
  if (temFalha) return TRACKING_STATUS.PENDENTE

  const temPendente = lista.some(
    (cliente) => cliente && cliente.status === FEE_BILLING_CLIENT_STATUS.PENDENTE
  )
  if (temPendente) return TRACKING_STATUS.EM_ANDAMENTO

  if (lista.length > 0) return TRACKING_STATUS.CONCLUIDO

  return TRACKING_STATUS.EM_ANDAMENTO
}
